'use client';

import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogOverlay, 
  DialogPortal,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { BookService } from '../services/bookService';
import BookComposer from './components/composer/book-composer';

// Define Book interface to match API structure
interface BookData {
  id: string;
  title: string;
  name?: string;
  description: string;
  category: string;
  duration: string;
  trainingType: string;
  status: string;
  certification: boolean;
  createdDate: string;
  lastModified: string;
  modules: BookModule[];
  // Optional fields
  createdBy?: string;
  author?: string;
  createdAt?: string;
  updatedAt?: string;
  level?: string;
  course_type?: string;
}

interface BookModule {
  module_id: string;
  name: string;
  type: string;
  duration: number;
  start_day_offset: number;
  start_day_offset_relative_to: 'course' | 'module' | 'section';
  start_day_offset_reference_id?: string;
  order: number;
  sections: BookSection[];
}

interface BookSection {
  section_id: string;
  name: string;
  order: number;
  duration: number;
  type: string;
  start_day_offset: number;
  start_day_offset_relative_to: 'course' | 'module' | 'section';
  start_day_offset_reference_id?: string;
  file_id: string;
  file: {
    file_id: string;
    file_name: string;
    file_type: string;
    file_size: number;
    file_url: string;
  };
}

interface FullscreenBookEditDialogProps {
  bookId: string;
  onClose?: () => void;
}

export function FullscreenBookEditDialog({ bookId, onClose }: FullscreenBookEditDialogProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [bookData, setBookData] = useState<BookData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBookData = async () => {
      try {
        setIsLoading(true);
        const response = await BookService.getBookDetails(bookId);
        const data = response.data || response;
        setBookData(data);
        setError(null);
      } catch (err) {
        setError('Không thể tải thông tin sách');
        console.error('Error fetching book data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookData();
  }, [bookId]);

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogPortal>
        <DialogOverlay className="bg-gray-800/80 backdrop-blur-sm" />
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="flex h-screen w-screen">
            <DialogContent 
              className="m-0 p-0 border-0 bg-white dark:bg-gray-900 max-w-none w-full h-full shadow-none"
              onInteractOutside={(e) => e.preventDefault()} // Prevent closing when clicking outside
              onEscapeKeyDown={(e) => e.preventDefault()} // Prevent closing with Esc key
            >
              <DialogTitle className="sr-only">Chỉnh sửa sách</DialogTitle>
              {isLoading ? (
                <div className="flex items-center justify-center h-full w-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-300">Đang tải...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center h-full w-full">
                  <div className="text-center">
                    <div className="text-red-600 mb-4">{error}</div>
                    <Button onClick={() => window.location.reload()}>
                      Thử lại
                    </Button>
                    <Button variant="outline" onClick={handleClose} className="ml-2">
                      Quay lại
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="h-full w-full flex flex-col">
                  {/* Header with close button */}
                  <div className="flex items-center justify-between px-4 py-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={handleClose}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                        <span className="ml-2">Đóng</span>
                      </Button>
                    </div>
                    
                    <div className="text-center flex-1">
                      <h1 className="font-bold truncate">{bookData?.title || bookData?.name || 'Chỉnh sửa sách'}</h1>
                    </div>
                    
                    <div className="w-20"></div> {/* Spacer to balance the header */}
                  </div>
                  
                  {/* Book Content Composer Component */}
                  <div className="flex-1 overflow-auto">
                    <BookComposer bookId={bookId} itemId="" itemType="book" />
                  </div>
                </div>
              )}
            </DialogContent>
          </div>
        </div>
      </DialogPortal>
    </Dialog>
  );
}